/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { getPayloadObject, getTelemetryAttributes, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { ChannelConfiguration } from '@msdyn365-commerce/retail-proxy';
import { StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import * as Msdyn365 from '@msdyn365-commerce/core';
import { createAzureMapsInstance } from '../../functions/azure-maps';
import SuggestionDropdownComponent from '../suggestion-dropdown';

export interface IAddressInput {
    id?: string;
    name?: string;
    className: string;
    type: string;
    value: string;
    maxLength?: number;
    autoFocus?: boolean;
    additionalAddributes?: object;
    telemetryContent?: ITelemetryContent;
    onChange(event: React.ChangeEvent<HTMLInputElement>): void;
    channel?: ChannelConfiguration;
    locale?: string;
    isMapAPILoaded?: boolean;
    onAzureMapsSuggestionSelected?(suggestion: ISuggestion): void;
    azureMapsDebounceDelay?: number;
}
export interface ISuggestion {
    formattedSuggestion: string;
    title: string;
    subtitle: string;

    address: ISearchResultAddress;
}
interface IStoreSelectorSearchFormState {
    suggestions: ISuggestion[];
    activeSuggestionId: string;
    activeSuggestionIndex: number;
    value: string;
}
interface ISearchResultAddress {
    streetNumber?: string;
    streetName?: string;
    municipality?: string;
    countrySecondarySubdivision?: string;
    countrySubdivision?: string;
    postalCode?: string;
    extendedPostalCode?: string;
    countryCode?: string;
    country?: string;
    countryCodeISO3?: string;
    freeformAddress?: string;
    countrySubdivisionName?: string;
    localName?: string;
    countrySubdivisionCode?: string;
}

/**
 * Address input.
 * @param props - Configuration of the functional component.
 * @returns React functional component.
 */
export const AddressInputFunctionComponent: React.FC<IAddressInput> = (props: IAddressInput) => {
    const {
        id: itemId,
        name,
        className,
        type,
        value,
        maxLength,
        additionalAddributes,
        autoFocus: shouldUseAutoFocus,
        telemetryContent,
        locale,
        channel,
        onChange,
        onAzureMapsSuggestionSelected,
        azureMapsDebounceDelay
    } = props;
    const telemetry: Msdyn365.ITelemetry = {} as Msdyn365.ITelemetry;

    const [state, setState] = React.useState<IStoreSelectorSearchFormState>({
        suggestions: [],
        activeSuggestionId: '',
        activeSuggestionIndex: -1,
        value: props.value || ''
    });
    // Ref to track the input element for dropdown
    const inputRef = React.useRef<HTMLInputElement>(null);

    const payLoad = getPayloadObject('click', telemetryContent!, name!);
    const attributes = getTelemetryAttributes(telemetryContent!, payLoad);

    // Initialize Azure Maps instance once
    const azureMapsInstance = React.useMemo(() => {
        if (channel && locale) {
            return createAzureMapsInstance(channel, locale, telemetry);
        }
        return null;
    }, [channel, locale]);

    const onTextInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue: string = event.target.value;
        setState(prevState => ({
            ...prevState,
            value: inputValue
        }));
        const streetInputFieldName: string = 'Street';
        if (!StringExtensions.isNullOrWhitespace(inputValue) && name === streetInputFieldName && azureMapsInstance) {
            azureMapsInstance
                .getSuggestions(inputValue, azureMapsDebounceDelay)
                .then(suggestions => {
                    setState(prevState => ({
                        ...prevState,
                        suggestions: suggestions
                    }));
                })
                .catch(() => {
                    setState(prevState => ({
                        ...prevState,
                        suggestions: []
                    }));
                });
        } else if (inputValue === '') {
            setState(prevState => ({
                ...prevState,
                suggestions: []
            }));
        }
        onChange(event);
    };

    const _onSuggestionSelected = (suggestion: ISuggestion): void => {
        setState((prevState: any) => ({
            ...prevState,
            value: suggestion.formattedSuggestion,
            suggestions: []
        }));
        if (onAzureMapsSuggestionSelected) {
            onAzureMapsSuggestionSelected(suggestion);
        }
    };

    return (
        <div>
            <input
                ref={inputRef}
                name={name}
                id={itemId}
                className={`${className}__input ${className}__input-${type}`}
                type={type}
                autoFocus={shouldUseAutoFocus}
                value={value}
                maxLength={maxLength}
                {...(additionalAddributes || {})}
                {...attributes}
                onChange={onTextInputChange}
            />
            {name === 'Street' && (
                <SuggestionDropdownComponent
                    suggestions={state.suggestions}
                    inputId={itemId}
                    inputElement={inputRef.current}
                    onSuggestionSelected={_onSuggestionSelected}
                />
            )}
        </div>
    );
};

export default AddressInputFunctionComponent;
